// src/components/setting/AdvertisementManager.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import { ref, onValue, update, set, remove } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { contentUsersDatabase } from '../../firebase'; // 新しいDBのみをインポート
import shared from '../../styles/shared.module.css';
import styles from './ProductManager.module.css';

const AdvertisementManager = () => {
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [advertisements, setAdvertisements] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // 新規広告と編集中の広告のフォームデータ
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    adLabel: '',
    adUrl: '',
    position: 'alcohol', // デフォルト位置
    priority: 1,
    isHidden: false,
    image: null,
    startDate: '', // YYYY-MM-DD形式
    endDate: '', // YYYY-MM-DD形式
  });

  const fileInputRef = useRef(null);
  const popupRef = useRef(null);

  // 位置オプション
  const positionOptions = [
    { value: 'alcohol', label: 'アルコール' },
    { value: 'nonalcohol', label: 'ノンアルコール' },
    { value: 'food', label: 'フード' },
    { value: 'all', label: '全カテゴリ' }
  ];

  // 管理者権限チェック
  useEffect(() => {
    const checkAdmin = async () => {
      if (currentUser) {
        try {
          const adminStatus = await isAdmin(currentUser.uid);
          setIsAdminUser(adminStatus);

          // 管理者でない場合はダッシュボードにリダイレクト
          if (!adminStatus) {
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('管理者権限チェックエラー:', error);
          navigate('/dashboard');
        }
      }
    };

    checkAdmin();
  }, [currentUser, isAdmin, navigate]);

  // 広告データを取得
  useEffect(() => {
    const fetchAdvertisements = async () => {
      try {
        // 新しいDBから広告データを取得
        const db = contentUsersDatabase;
        const adsRef = ref(db, 'advertisements/items');

        onValue(adsRef, (snapshot) => {
          const data = snapshot.val();
          if (data) {
            // オブジェクトを配列に変換し、IDを追加
            const adsArray = Object.keys(data).map(key => ({
              id: key,
              ...data[key],
              // 表示/非表示状態を統一
              isHidden: data[key].status === 'hidden' || data[key].isHidden === true,
              // 画像URLを正規化
              image: data[key].imageUrl || data[key].image
            }));

            // 優先度順にソート
            const sortedAds = adsArray.sort((a, b) => {
              return (a.priority || 0) - (b.priority || 0);
            });

            setAdvertisements(sortedAds);
          } else {
            setAdvertisements([]);
          }
          setLoading(false);
        }, (error) => {
          console.error('広告データの取得エラー:', error);
          setError('広告データの取得に失敗しました: ' + error.message);
          setLoading(false);
        });
      } catch (error) {
        console.error('広告データの取得エラー:', error);
        setError('広告データの取得に失敗しました。');
        setLoading(false);
      }
    };

    fetchAdvertisements();
  }, []);

  // ポップアップ外のクリックを検知
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        handleClosePopup();
      }
    };

    if (showEditPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditPopup]);

  // 広告を選択して編集モードに入る
  const handleSelectAd = (ad) => {
    setSelectedAd(ad);

    // 日付のフォーマット変換（ISOからYYYY-MM-DD）
    const formatDate = (isoDate) => {
      if (!isoDate) return '';
      return isoDate.substring(0, 10); // YYYY-MM-DD部分だけを取得
    };

    setFormData({
      name: ad.name || '',
      description: ad.description || '',
      adLabel: ad.adLabel || '',
      adUrl: ad.adUrl || '',
      position: ad.position || 'alcohol',
      priority: ad.priority || 1,
      isHidden: ad.isHidden || false,
      image: null, // 画像ファイルは初期状態では選択なし
      currentImageURL: ad.image || '',
      startDate: formatDate(ad.startDate),
      endDate: formatDate(ad.endDate),
    });

    setShowEditPopup(true);
    setEditMode(true);
    setShowAddForm(false);
    setConfirmDelete(false);
  };

  // ポップアップを閉じる
  const handleClosePopup = () => {
    setShowEditPopup(false);
    setTimeout(() => {
      setSelectedAd(null);
      setEditMode(false);
      resetForm();
      setConfirmDelete(false);
    }, 300); // アニメーションの時間に合わせる
  };

  // 新規広告追加フォームを表示
  const handleShowAddForm = () => {
    setShowAddForm(true);
    setEditMode(false);
    setSelectedAd(null);
    resetForm();
  };

  // 新規広告追加フォームをキャンセル
  const handleCancelAddForm = () => {
    setShowAddForm(false);
    resetForm();
  };

  // フォームをリセット
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      adLabel: '',
      adUrl: '',
      position: 'alcohol',
      priority: 1,
      isHidden: false,
      image: null,
      startDate: '',
      endDate: '',
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 広告の表示/非表示を切り替え
  const handleToggleVisibility = async (ad, e) => {
    e.stopPropagation(); // 親要素のクリックイベントを防止

    try {
      const db = contentUsersDatabase;
      const adRef = ref(db, `advertisements/items/${ad.id}`);

      // 新しいDBの構造に合わせて更新データを作成
      const updateData = {
        status: ad.isHidden ? 'active' : 'hidden',
        updatedAt: new Date().toISOString()
      };

      await update(adRef, updateData);

      // 成功メッセージを表示しない
    } catch (error) {
      console.error('広告表示状態の変更エラー:', error);
      setError(`広告表示状態の変更に失敗しました: ${error.message}`);
    }
  };

  // 削除確認を表示
  const handleShowDeleteConfirm = () => {
    setConfirmDelete(true);
  };

  // 広告を削除
  const handleDeleteAd = async () => {
    if (!selectedAd) return;

    try {
      const db = contentUsersDatabase;
      const adRef = ref(db, `advertisements/items/${selectedAd.id}`);

      // 画像がある場合はStorage上の画像も削除
      if (selectedAd.image && selectedAd.image.includes('firebasestorage')) {
        try {
          // URLからストレージのパスを抽出する試み
          const storage = getStorage();
          const urlParts = selectedAd.image.split('/');
          const imageName = urlParts[urlParts.length - 1].split('?')[0];
          const imageRef = storageRef(storage, `advertisements/${imageName}`);

          await deleteObject(imageRef);
          console.log(`広告画像を削除しました: ${imageName}`);
        } catch (imageError) {
          console.warn('画像の削除に失敗しましたが、広告削除を続行します:', imageError);
        }
      }

      // Realtime Database上の広告を削除
      await remove(adRef);

      setSuccess(`広告「${selectedAd.name}」を削除しました`);
      handleClosePopup();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('広告削除エラー:', error);
      setError(`広告削除に失敗しました: ${error.message}`);
    }
  };

  // フォーム入力の変更を処理
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'priority') {
      // 整数変換
      const numValue = parseInt(value, 10);
      setFormData(prev => ({
        ...prev,
        [name]: isNaN(numValue) ? 1 : numValue
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // 画像ファイル選択時の処理
  const handleImageChange = (e) => {
    if (e.target.files.length > 0) {
      setFormData(prev => ({
        ...prev,
        image: e.target.files[0]
      }));
    }
  };

  // 画像ファイルをアップロード
  const uploadImage = async (file) => {
    if (!file) return null;

    const storage = getStorage();
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const imageRef = storageRef(storage, `advertisements/${fileName}`);

    await uploadBytes(imageRef, file);
    const downloadURL = await getDownloadURL(imageRef);

    return downloadURL;
  };

  // 新しい広告IDを生成
  const generateNewAdId = () => {
    const timestamp = Date.now();
    return `ad-${timestamp}`;
  };

  // 広告を保存（新規または更新）
  const handleSaveAd = async (e) => {
    e.preventDefault();

    if (!isAdminUser) {
      setError('この操作を行うには管理者権限が必要です。');
      return;
    }

    if (!formData.name.trim()) {
      setError('広告名を入力してください。');
      return;
    }

    if (!formData.adUrl.trim() && !formData.adLabel.trim()) {
      setError('広告ラベルまたは広告URLを入力してください。');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const db = contentUsersDatabase;
      let imageURL = formData.currentImageURL || '';

      // 画像ファイルがある場合はアップロード
      if (formData.image) {
        imageURL = await uploadImage(formData.image);
      }

      // ISO日付形式に変換
      const formatDateToISO = (date) => {
        if (!date) return null;
        return new Date(`${date}T00:00:00.000Z`).toISOString();
      };

      // 保存するデータ - 新しいDBの構造に合わせる
      const adData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        adLabel: formData.adLabel.trim(),
        adUrl: formData.adUrl.trim(),
        position: formData.position,
        priority: formData.priority,
        status: formData.isHidden ? 'hidden' : 'active', // isHiddenをstatusに変換
        imageUrl: imageURL, // imageをimageUrlに変換
        startDate: formatDateToISO(formData.startDate),
        endDate: formatDateToISO(formData.endDate),
        updatedAt: new Date().toISOString()
      };

      if (editMode && selectedAd) {
        // 既存広告の更新
        const adRef = ref(db, `advertisements/items/${selectedAd.id}`);
        await update(adRef, adData);
        setSuccess(`広告「${adData.name}」を更新しました`);
      } else {
        // 新規広告の追加 - 新しいIDを生成
        const newAdId = generateNewAdId();
        const adRef = ref(db, `advertisements/items/${newAdId}`);

        await set(adRef, {
          ...adData,
          id: newAdId,
          createdAt: new Date().toISOString()
        });
        setSuccess(`広告「${adData.name}」を追加しました (ID: ${newAdId})`);
      }

      // フォームをリセットして編集モードを終了
      handleClosePopup();
      setShowAddForm(false);

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('広告保存エラー:', error);
      setError(`広告の保存に失敗しました: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 現在日時との比較で広告の状態を判定する
  // const getAdStatus = (ad) => {
  //   if (ad.isHidden) return 'hidden';

  //   const now = new Date();

  //   // 開始日が設定されていて、まだ開始前の場合
  //   if (ad.startDate && new Date(ad.startDate) > now) {
  //     return 'upcoming';
  //   }

  //   // 終了日が設定されていて、既に終了している場合
  //   if (ad.endDate && new Date(ad.endDate) < now) {
  //     return 'expired';
  //   }

  //   return 'active';
  // };

  if (loading && advertisements.length === 0) {
    return (
      <div className={shared.loadingSpinnerShort}>
        <div className={shared.spinner}></div>
      </div>
    );
  }

  return (
    <div className={styles.productManagerContainer}>
      {error && (
        <div className={styles.errorMessage}>
          {error}
          <button onClick={() => setError('')} className={styles.closeButton}>×</button>
        </div>
      )}

      {success && (
        <div className={styles.successMessage}>
          {success}
          <button onClick={() => setSuccess('')} className={styles.closeButton}>×</button>
        </div>
      )}

      <div className={styles.controlPanel}>
        <button
          onClick={handleShowAddForm}
          disabled={editMode || showAddForm}
          className={styles.addButton}
        >
          新規広告を追加
        </button>
      </div>

      {/* 新規広告追加フォーム */}
      {showAddForm && (
        <div className={styles.formCard}>
          <h3 className={styles.formTitle}>新規広告の追加</h3>
          <form onSubmit={handleSaveAd}>
            <div className={styles.formGroup}>
              <label className={styles.label}>広告名 <span className={styles.required}>*</span></label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                className={styles.input}
                required
              />
              <p className={styles.helperText}>メニューに表示される商品名になります</p>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>広告ラベル</label>
              <input
                type="text"
                name="adLabel"
                value={formData.adLabel}
                onChange={handleFormChange}
                className={styles.input}
                placeholder="坂内おすすめウヰスキー"
              />
              <p className={styles.helperText}>価格の代わりに表示されるテキスト</p>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>広告URL</label>
              <input
                type="text"
                name="adUrl"
                value={formData.adUrl}
                onChange={handleFormChange}
                className={styles.input}
                placeholder="https://example.com/product"
              />
              <p className={styles.helperText}>クリック時に移動するURL</p>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>配置場所</label>
                <select
                  name="position"
                  value={formData.position}
                  onChange={handleFormChange}
                  className={styles.input}
                >
                  {positionOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>優先度</label>
                <input
                  type="number"
                  name="priority"
                  value={formData.priority}
                  onChange={handleFormChange}
                  min="1"
                  max="10"
                  className={styles.input}
                />
                <p className={styles.helperText}>数値が小さいほど優先表示されます</p>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>広告説明</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                className={styles.textarea}
                rows={2}
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>開始日</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleFormChange}
                  className={styles.input}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>終了日</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleFormChange}
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>広告画像</label>
              <input
                type="file"
                onChange={handleImageChange}
                className={styles.fileInput}
                accept="image/*"
                ref={fileInputRef}
              />
              <p className={styles.helperText}>画像がない場合はプレースホルダーが使用されます</p>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="isHidden"
                  checked={formData.isHidden}
                  onChange={handleFormChange}
                  className={styles.checkbox}
                />
                メニューに表示しない
              </label>
            </div>

            <div className={styles.formActions}>
              <button
                type="submit"
                className={styles.saveButton}
                disabled={loading}
              >
                {loading ? '保存中...' : '広告を追加'}
              </button>
              <button
                type="button"
                onClick={handleCancelAddForm}
                className={styles.cancelButton}
                disabled={loading}
              >
                キャンセル
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 編集ポップアップ */}
      {showEditPopup && selectedAd && (
        <div className={styles.modalOverlay}>
          <div
            className={styles.modalContent}
            ref={popupRef}
          >
            <button
              className={styles.modalCloseButton}
              onClick={handleClosePopup}
            >
              ×
            </button>

            <h3 className={styles.modalTitle}>広告の編集: {selectedAd.name}</h3>

            {confirmDelete ? (
              <div className={styles.deleteConfirm}>
                <p className={styles.deleteConfirmText}>
                  本当に広告「{selectedAd.name}」を削除しますか？<br />
                  この操作は元に戻せません。
                </p>
                <div className={styles.deleteConfirmButtons}>
                  <button
                    className={styles.deleteConfirmButton}
                    onClick={handleDeleteAd}
                  >
                    削除する
                  </button>
                  <button
                    className={styles.cancelDeleteButton}
                    onClick={() => setConfirmDelete(false)}
                  >
                    キャンセル
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSaveAd}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>広告名 <span className={styles.required}>*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    className={styles.input}
                    required
                  />
                  <p className={styles.helperText}>メニューに表示される商品名になります</p>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>広告ラベル</label>
                  <input
                    type="text"
                    name="adLabel"
                    value={formData.adLabel}
                    onChange={handleFormChange}
                    className={styles.input}
                    placeholder="坂内おすすめウヰスキー"
                  />
                  <p className={styles.helperText}>価格の代わりに表示されるテキスト</p>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>広告URL</label>
                  <input
                    type="text"
                    name="adUrl"
                    value={formData.adUrl}
                    onChange={handleFormChange}
                    className={styles.input}
                    placeholder="https://example.com/product"
                  />
                  <p className={styles.helperText}>クリック時に移動するURL</p>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>配置場所</label>
                    <select
                      name="position"
                      value={formData.position}
                      onChange={handleFormChange}
                      className={styles.input}
                    >
                      {positionOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>優先度</label>
                    <input
                      type="number"
                      name="priority"
                      value={formData.priority}
                      onChange={handleFormChange}
                      min="1"
                      max="10"
                      className={styles.input}
                    />
                    <p className={styles.helperText}>数値が小さいほど優先表示されます</p>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>広告説明</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    className={styles.textarea}
                    rows={2}
                  />
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>開始日</label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleFormChange}
                      className={styles.input}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>終了日</label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleFormChange}
                      className={styles.input}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>現在の画像</label>
                  {formData.currentImageURL ? (
                    <div className={styles.currentImageContainer}>
                      <img
                        src={formData.currentImageURL}
                        alt={formData.name}
                        className={styles.currentImage}
                      />
                    </div>
                  ) : (
                    <p className={styles.noImageText}>画像がありません</p>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>新しい画像 (変更する場合のみ)</label>
                  <input
                    type="file"
                    onChange={handleImageChange}
                    className={styles.fileInput}
                    accept="image/*"
                    ref={fileInputRef}
                  />
                  <p className={styles.helperText}>画像を変更しない場合は現在の画像が使用されます</p>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="isHidden"
                      checked={formData.isHidden}
                      onChange={handleFormChange}
                      className={styles.checkbox}
                    />
                    メニューに表示しない
                  </label>
                </div>

                <div className={styles.formActions}>
                  <button
                    type="submit"
                    className={styles.saveButton}
                    disabled={loading}
                  >
                    {loading ? '保存中...' : '変更を保存'}
                  </button>
                  <button
                    type="button"
                    onClick={handleShowDeleteConfirm}
                    className={styles.deleteButtonInPopup}
                  >
                    この広告を削除
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* 広告一覧表示 */}
      {!loading && advertisements.length > 0 && !editMode && !showAddForm && (
        <div className={styles.productListContainer}>
          <div className={styles.tableHeader}>
            <div className={styles.colName}>広告名</div>
            <div className={styles.colType}>配置</div>
            <div className={styles.colPrice}>期間</div>
            <div className={styles.colActions}>操作</div>
          </div>

          {advertisements.map(ad => {
            // const adStatus = getAdStatus(ad);

            return (
              <div
                key={ad.id}
                className={`${styles.productRow} ${ad.isHidden ? styles.productRowHidden : ''}`}
                onClick={() => handleSelectAd(ad)}
              >
                <div className={styles.colName}>
                  <div className={styles.productInfo}>
                    <div className={styles.productImageThumb}>
                      <img
                        src={ad.image || "/api/placeholder/60/60"}
                        alt={ad.name}
                        className={styles.thumbImage}
                      />
                    </div>
                    <div className={styles.productNameWrapper}>
                      <span className={styles.productName}>{ad.name}</span>
                      <span className={styles.productDesc}>
                        {ad.adLabel || ad.adUrl || '広告リンクなし'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className={styles.colType}>
                  {positionOptions.find(opt => opt.value === ad.position)?.label || '未設定'}
                </div>
                <div className={styles.colPrice}>
                  {ad.startDate && ad.endDate ? (
                    <span>
                      {new Date(ad.startDate).toLocaleDateString()} - {new Date(ad.endDate).toLocaleDateString()}
                    </span>
                  ) : ad.startDate ? (
                    <span>{new Date(ad.startDate).toLocaleDateString()} から</span>
                  ) : ad.endDate ? (
                    <span>～ {new Date(ad.endDate).toLocaleDateString()}</span>
                  ) : (
                    <span>無期限</span>
                  )}
                </div>
                <div className={styles.colActions}>
                  <div className={styles.actionButtons}>
                    <button
                      onClick={(e) => handleToggleVisibility(ad, e)}
                      className={ad.isHidden ? styles.showButton : styles.hideButton}
                      title={ad.isHidden ? '表示する' : '非表示にする'}
                    >
                      {ad.isHidden ? '👁' : '🚫'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && advertisements.length === 0 && !showAddForm && (
        <div className={styles.emptyState}>
          <p className={styles.emptyStateText}>
            広告が登録されていません。「新規広告を追加」ボタンをクリックして広告を登録してください。
          </p>
        </div>
      )}
    </div>
  );
};

export default AdvertisementManager;